import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { useDiscovery } from '@/context/DiscoveryContext';
import { useCompliance } from '@/context/ComplianceContext';
import { apiUrl, mediaUrl } from '@/api/config';
import axios from 'axios';

const PromoModal = ({ onComplete = () => {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { promotions: contextPromotions, loadingPromotions: contextLoading } = useDiscovery();
  const [localPromotions, setLocalPromotions] = useState([]);
  const [dismissedPromoIds, setDismissedPromoIds] = useState(() => {
    try {
      const stored = sessionStorage.getItem('dismissed_promo_modal_ids');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [localLoading, setLocalLoading] = useState(true);
  const { current_platform } = useCompliance();

  // Fetch promotions independently to ensure sync with banner
  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const response = await axios.get(apiUrl('superadmin/promotions/'), {
          params: { platform: 'StudentPortal', is_active: 'true' }
        });
        const data = response.data;
        const list = Array.isArray(data) ? data : (data.results || []);
        setLocalPromotions(list);
      } catch (error) {
        console.error('[PromoModal] Failed to fetch local promotions:', error);
      } finally {
        setLocalLoading(false);
      }
    };
    fetchPromos();
  }, []);
  
  // All active promotions available on this platform
  const promotions = useMemo(() => {
    const allPromos = localPromotions.length > 0 ? localPromotions : contextPromotions;
    
    return (allPromos || [])
      .filter(p => p.is_active !== false)
      .filter(p => {
        // Platform specific visibility filtering
        if (current_platform === 'webapp') return p.visible_on_webapp !== false;
        if (current_platform === 'ios') return p.visible_on_ios !== false;
        if (current_platform === 'android') return p.visible_on_android !== false;
        return true;
      })
      .sort((a, b) => (a.order || a.order_number || 0) - (b.order || b.order_number || 0));
  }, [contextPromotions, localPromotions, current_platform]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState({});

  // Auto-open logic on load: only auto-open if there are undismissed promotions
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current) return;
    if (localLoading && contextLoading) return;

    if (promotions.length > 0) {
      const undismissed = promotions.filter(p => !dismissedPromoIds.includes(p.id));
      if (undismissed.length > 0) {
        hasAutoOpenedRef.current = true;
        const initialIndex = promotions.findIndex(p => p.id === undismissed[0].id);
        if (initialIndex !== -1) {
          setCurrentIndex(initialIndex);
        }
        const timer = setTimeout(() => {
          setIsOpen(true);
        }, 800);
        return () => clearTimeout(timer);
      } else {
        hasAutoOpenedRef.current = true;
        onComplete();
      }
    } else if (!localLoading && !contextLoading) {
      hasAutoOpenedRef.current = true;
      onComplete();
    }
  }, [promotions, localLoading, contextLoading, dismissedPromoIds, onComplete]);

  // Listen for explicit banner click to open modal
  useEffect(() => {
    const handleOpenExternal = (event) => {
      if (event.detail && event.detail.promotionId) {
        const index = promotions.findIndex(p => p.id === event.detail.promotionId);
        if (index !== -1) {
          setCurrentIndex(index);
        }
      }
      setIsOpen(true);
    };

    window.addEventListener('open-promo-modal', handleOpenExternal);
    return () => window.removeEventListener('open-promo-modal', handleOpenExternal);
  }, [promotions]);

  // Auto-scrolling logic
  useEffect(() => {
    if (!isOpen || promotions.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % promotions.length);
    }, 5000); // 5 seconds interval

    return () => clearInterval(interval);
  }, [isOpen, promotions.length]);

  const handleManualClose = () => {
    setIsOpen(false);
    const visibleIds = promotions.map(p => p.id);
    const updatedDismissed = Array.from(new Set([...dismissedPromoIds, ...visibleIds]));
    setDismissedPromoIds(updatedDismissed);
    try {
      sessionStorage.setItem('dismissed_promo_modal_ids', JSON.stringify(updatedDismissed));
    } catch (error) {
      console.error('[PromoModal] Failed to store dismissed IDs:', error);
    }
    onComplete();
  };

  const nextPromo = (e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % promotions.length);
  };

  const prevPromo = (e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + promotions.length) % promotions.length);
  };

  const handleImageLoad = (id) => {
    setImagesLoaded(prev => ({ ...prev, [id]: true }));
  };

  // IF NO ACTIVE PROMOS OR NOT OPEN, DON'T BLOCK
  if (promotions.length === 0) return null;

  const currentPromo = promotions[currentIndex] || promotions[0];
  if (!currentPromo) return null;

  const rawPoster = currentPromo.poster_image_url || currentPromo.poster_image;
  const posterUrl = rawPoster ? mediaUrl(rawPoster) : null;
  const isCurrentImageLoaded = imagesLoaded[currentPromo.id];

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) handleManualClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[11000] bg-black/90 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />

        <DialogPrimitive.Content
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className={cn(
            "fixed z-[11001] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-1.5rem)] rounded-2xl h-auto max-h-[calc(100dvh-(env(safe-area-inset-top)+env(safe-area-inset-bottom)+9rem))] flex flex-col items-center justify-center p-0 outline-none transition-all duration-300 overflow-y-auto overflow-x-hidden",
            "md:max-h-[90vh] md:max-w-[85vw] md:w-fit"
          )}
        >
          <div className="relative flex flex-col items-center w-full">
            <DialogPrimitive.Title className="sr-only">Promotions</DialogPrimitive.Title>

            <div className="relative flex items-center justify-center w-full">
              {promotions.length > 1 && isCurrentImageLoaded && (
                <>
                  <button onClick={prevPromo} className="absolute left-4 p-3 bg-black/30 hover:bg-black/50 text-white rounded-full transition-all backdrop-blur-md z-[70] hidden sm:flex border border-white/20 active:scale-95 group">
                    <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 transition-transform group-hover:-translate-x-1" />
                  </button>
                  <button onClick={nextPromo} className="absolute right-4 p-3 bg-black/30 hover:bg-black/50 text-white rounded-full transition-all backdrop-blur-md z-[70] hidden sm:flex border border-white/20 active:scale-95 group">
                    <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 transition-transform group-hover:translate-x-1" />
                  </button>
                </>
              )}

              <div className="relative w-full sm:w-auto overflow-visible group">
                <div className="absolute top-3 right-3 sm:top-5 sm:right-5 z-[80]">
                  <button
                    onClick={handleManualClose}
                    className="group p-2 sm:p-2.5 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all duration-300 hover:rotate-90 focus:outline-none border border-white/30 backdrop-blur-md shadow-lg"
                    aria-label="Close promotion dialog"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6 transition-transform group-active:scale-90" />
                  </button>
                </div>

                <div className="relative overflow-hidden rounded-[12px] sm:rounded-[24px] shadow-[0_40px_80px_rgba(0,0,0,0.9)] flex items-center justify-center bg-black/40">
                  {!isCurrentImageLoaded && (
                    <div className="absolute inset-0 z-10 overflow-hidden bg-white/5 min-h-[300px] sm:min-w-[400px]">
                      <div className="w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" style={{ backgroundSize: '200% 100%' }}></div>
                    </div>
                  )}

                  <img
                    src={posterUrl}
                    alt={`Promotion ${currentIndex + 1}`}
                    onLoad={() => handleImageLoad(currentPromo.id)}
                    onError={() => handleImageLoad(currentPromo.id)}
                    className={cn(
                      "w-full sm:w-auto h-auto max-w-full max-h-[75vh] md:max-h-[80vh] object-contain select-none align-middle block transition-opacity duration-700",
                      isCurrentImageLoaded ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </div>
              </div>
            </div>

            {isCurrentImageLoaded && (
              <div className="flex flex-col items-center gap-3 mt-4 sm:mt-6 w-full max-w-[280px] sm:max-w-[320px] animate-in fade-in slide-in-from-bottom-2 duration-700">
                {promotions.length > 1 && (
                  <div className="flex sm:hidden items-center gap-6 mb-1">
                    <button onClick={prevPromo} className="p-2.5 bg-white/10 text-white rounded-full active:scale-95"><ChevronLeft className="w-5 h-5" /></button>
                    <div className="flex gap-1.5">
                      {promotions.map((_, idx) => (
                        <div key={idx} className={cn("w-1.5 h-1.5 rounded-full transition-all duration-300", idx === currentIndex ? 'bg-[hsl(var(--green-bright))] w-5' : 'bg-white/30')} />
                      ))}
                    </div>
                    <button onClick={nextPromo} className="p-2.5 bg-white/10 text-white rounded-full active:scale-95"><ChevronRight className="w-5 h-5" /></button>
                  </div>
                )}

                {currentPromo.button_name && currentPromo.url && (
                  <div className="w-full">
                    <a href={currentPromo.url} target="_blank" rel="noopener noreferrer" className="block group" onClick={() => handleManualClose()}>
                      <Button className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold py-3.5 sm:py-4 text-base sm:text-lg rounded-xl sm:rounded-2xl shadow-lg shadow-emerald-600/30 transform transition-all duration-300 group-hover:scale-[1.02] active:scale-[0.98] border border-white/20 uppercase tracking-widest flex items-center justify-center gap-2">
                        {currentPromo.button_name}
                        <span className="text-lg sm:text-xl group-hover:translate-x-1 transition-transform">→</span>
                      </Button>
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2.5s infinite linear;
        }
      `}</style>
    </DialogPrimitive.Root>
  );
};

export default PromoModal;
